import Stripe from 'stripe';
import type {
  DonationTier,
  DonationCheckoutOptions,
  DonationCheckoutResult,
} from '../../shared/types/donation';

export interface CreateCheckoutSessionOptions {
  priceId: string;
  customerId?: string;
  successUrl: string;
  cancelUrl: string;
  mode?: 'payment' | 'subscription' | 'setup';
  metadata?: Record<string, string>;
}

export interface ProcessPaymentOptions {
  amount: number;
  currency: string;
  paymentMethodId: string;
  customerId?: string;
  description?: string;
  metadata?: Record<string, string>;
}

export interface CreateSubscriptionOptions {
  customerId: string;
  priceId: string;
  paymentMethodId?: string;
  trialPeriodDays?: number;
  metadata?: Record<string, string>;
}

export interface UpdateSubscriptionOptions {
  subscriptionId: string;
  priceId?: string;
  quantity?: number;
  metadata?: Record<string, string>;
}

/**
 * Create a Stripe checkout session
 */
export const createStripeCheckoutSession = async (
  stripe: Stripe,
  options: CreateCheckoutSessionOptions
): Promise<Stripe.Checkout.Session> => {
  const {
    priceId,
    customerId,
    successUrl,
    cancelUrl,
    mode = 'subscription',
    metadata = {},
  } = options;

  const sessionParams: Stripe.Checkout.SessionCreateParams = {
    mode,
    success_url: successUrl,
    cancel_url: cancelUrl,
    line_items: [
      {
        price: priceId,
        quantity: 1,
      },
    ],
    metadata,
  };

  if (customerId) {
    sessionParams.customer = customerId;
  } else {
    sessionParams.customer_creation = 'always';
  }

  if (mode === 'subscription') {
    sessionParams.subscription_data = {
      metadata,
    };
  }

  return await stripe.checkout.sessions.create(sessionParams);
};

/**
 * Process a one-time payment
 */
export const processPayment = async (
  stripe: Stripe,
  options: ProcessPaymentOptions
): Promise<Stripe.PaymentIntent> => {
  const {
    amount,
    currency,
    paymentMethodId,
    customerId,
    description,
    metadata = {},
  } = options;

  const paymentIntentParams: Stripe.PaymentIntentCreateParams = {
    amount,
    currency,
    payment_method: paymentMethodId,
    confirmation_method: 'manual',
    confirm: true,
    return_url: 'https://your-website.com/return',
    metadata,
  };

  if (customerId) {
    paymentIntentParams.customer = customerId;
  }

  if (description) {
    paymentIntentParams.description = description;
  }

  return await stripe.paymentIntents.create(paymentIntentParams);
};

/**
 * Create a subscription
 */
export const createSubscription = async (
  stripe: Stripe,
  options: CreateSubscriptionOptions
): Promise<Stripe.Subscription> => {
  const {
    customerId,
    priceId,
    paymentMethodId,
    trialPeriodDays,
    metadata = {},
  } = options;

  const subscriptionParams: Stripe.SubscriptionCreateParams = {
    customer: customerId,
    items: [
      {
        price: priceId,
      },
    ],
    metadata,
  };

  if (paymentMethodId) {
    subscriptionParams.default_payment_method = paymentMethodId;
  }

  if (trialPeriodDays) {
    subscriptionParams.trial_period_days = trialPeriodDays;
  }

  return await stripe.subscriptions.create(subscriptionParams);
};

/**
 * Update a subscription
 */
export const updateSubscription = async (
  stripe: Stripe,
  options: UpdateSubscriptionOptions
): Promise<Stripe.Subscription> => {
  const { subscriptionId, priceId, quantity, metadata } = options;

  const subscription = await stripe.subscriptions.retrieve(subscriptionId);

  const updateParams: Stripe.SubscriptionUpdateParams = {};

  if (priceId) {
    updateParams.items = [
      {
        id: subscription.items.data[0]?.id || '',
        price: priceId,
        quantity: quantity || 1,
      },
    ];
  }

  if (metadata) {
    updateParams.metadata = metadata;
  }

  return await stripe.subscriptions.update(subscriptionId, updateParams);
};

/**
 * Cancel a subscription
 */
export const cancelSubscription = async (
  stripe: Stripe,
  subscriptionId: string,
  cancelAtPeriodEnd: boolean = true
): Promise<Stripe.Subscription> => {
  if (cancelAtPeriodEnd) {
    return await stripe.subscriptions.update(subscriptionId, {
      cancel_at_period_end: true,
    });
  } else {
    return await stripe.subscriptions.cancel(subscriptionId);
  }
};

// ═══════════════════════════════════════════════════════════════
// DONATION CHECKOUT — inline price_data, zero Stripe Dashboard setup
// ═══════════════════════════════════════════════════════════════

/**
 * Create a donation checkout session using inline price_data.
 *
 * Unlike `createStripeCheckoutSession` which requires a pre-created Price ID,
 * this uses Stripe's inline `price_data` so apps don't need any Stripe
 * Dashboard setup. Stripe auto-creates the products on first use.
 *
 * Supports:
 * - Named tiers with fixed amounts (one-time or monthly)
 * - Custom amounts with configurable recurrence
 * - Metadata tagging for webhook identification
 *
 * @example
 * ```ts
 * const tiers: DonationTier[] = [
 *   { id: 'fan', name: 'Fan', description: 'Monthly $3 support', amountCents: 300, recurring: true },
 *   { id: 'gold', name: 'Gold', description: 'One-time $25 donation', amountCents: 2500 },
 * ];
 *
 * const result = await createDonationCheckout(stripe, tiers, {
 *   tier: 'fan',
 *   successUrl: 'https://example.com/thank-you?session_id={CHECKOUT_SESSION_ID}',
 *   cancelUrl: 'https://example.com/support',
 * });
 * // result.url → redirect user here
 * ```
 */
export const createDonationCheckout = async (
  stripe: Stripe,
  tiers: DonationTier[],
  options: DonationCheckoutOptions
): Promise<DonationCheckoutResult> => {
  const {
    tier,
    customAmountCents,
    customRecurring = false,
    successUrl,
    cancelUrl,
    metadata = {},
    customerId,
    allowPromotionCodes = true,
    billingAddressCollection = 'auto',
  } = options;

  let amountCents: number;
  let isRecurring: boolean;
  let productName: string;
  let productDescription: string;

  if (tier === 'custom') {
    if (!customAmountCents || customAmountCents < 100) {
      throw new Error(
        'Custom donations require an amount of at least $1.00 (100 cents)'
      );
    }
    amountCents = customAmountCents;
    isRecurring = customRecurring;
    productName = 'Custom Donation';
    productDescription = `Custom ${isRecurring ? 'monthly ' : ''}donation — $${(amountCents / 100).toFixed(2)}`;
  } else {
    const tierDef = tiers.find(t => t.id === tier);
    if (!tierDef) {
      throw new Error(`Unknown donation tier: ${tier}`);
    }
    amountCents = tierDef.amountCents;
    isRecurring = tierDef.recurring ?? false;
    productName = tierDef.name;
    productDescription = tierDef.description;
  }

  const priceData: Stripe.Checkout.SessionCreateParams.LineItem.PriceData = {
    currency: 'usd',
    product_data: {
      name: productName,
      description: productDescription,
    },
    unit_amount: amountCents,
    ...(isRecurring ? { recurring: { interval: 'month' as const } } : {}),
  };

  const sessionParams: Stripe.Checkout.SessionCreateParams = {
    mode: isRecurring ? 'subscription' : 'payment',
    ...(isRecurring ? {} : { submit_type: 'donate' as const }),
    line_items: [{ price_data: priceData, quantity: 1 }],
    success_url: successUrl,
    cancel_url: cancelUrl,
    metadata: {
      product_type: 'donation',
      tier,
      ...(tier === 'custom' ? { amount_cents: String(amountCents) } : {}),
      ...metadata,
    },
    billing_address_collection: billingAddressCollection,
    allow_promotion_codes: allowPromotionCodes,
  };

  if (customerId) {
    sessionParams.customer = customerId;
  }

  if (isRecurring) {
    sessionParams.subscription_data = {
      metadata: {
        product_type: 'donation',
        tier,
        ...metadata,
      },
    };
  }

  const session = await stripe.checkout.sessions.create(sessionParams);

  return {
    sessionId: session.id,
    url: session.url,
  };
};

/**
 * Validate Stripe webhook signature
 */
export const validateStripeSignature = (
  payload: string | Buffer,
  signature: string,
  endpointSecret: string
): Stripe.Event => {
  const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!);

  return stripe.webhooks.constructEvent(payload, signature, endpointSecret);
};

/**
 * Handle common Stripe webhook events
 */
export const handleStripeWebhook = async (
  event: Stripe.Event,
  handlers: {
    onPaymentSucceeded?: (paymentIntent: Stripe.PaymentIntent) => Promise<void>;
    onPaymentFailed?: (paymentIntent: Stripe.PaymentIntent) => Promise<void>;
    onSubscriptionCreated?: (
      subscription: Stripe.Subscription
    ) => Promise<void>;
    onSubscriptionUpdated?: (
      subscription: Stripe.Subscription
    ) => Promise<void>;
    onSubscriptionDeleted?: (
      subscription: Stripe.Subscription
    ) => Promise<void>;
    onInvoicePaid?: (invoice: Stripe.Invoice) => Promise<void>;
    onInvoicePaymentFailed?: (invoice: Stripe.Invoice) => Promise<void>;
  }
): Promise<void> => {
  switch (event.type) {
    case 'payment_intent.succeeded':
      if (handlers.onPaymentSucceeded) {
        await handlers.onPaymentSucceeded(
          event.data.object as Stripe.PaymentIntent
        );
      }
      break;

    case 'payment_intent.payment_failed':
      if (handlers.onPaymentFailed) {
        await handlers.onPaymentFailed(
          event.data.object as Stripe.PaymentIntent
        );
      }
      break;

    case 'customer.subscription.created':
      if (handlers.onSubscriptionCreated) {
        await handlers.onSubscriptionCreated(
          event.data.object as Stripe.Subscription
        );
      }
      break;

    case 'customer.subscription.updated':
      if (handlers.onSubscriptionUpdated) {
        await handlers.onSubscriptionUpdated(
          event.data.object as Stripe.Subscription
        );
      }
      break;

    case 'customer.subscription.deleted':
      if (handlers.onSubscriptionDeleted) {
        await handlers.onSubscriptionDeleted(
          event.data.object as Stripe.Subscription
        );
      }
      break;

    case 'invoice.paid':
      if (handlers.onInvoicePaid) {
        await handlers.onInvoicePaid(event.data.object as Stripe.Invoice);
      }
      break;

    case 'invoice.payment_failed':
      if (handlers.onInvoicePaymentFailed) {
        await handlers.onInvoicePaymentFailed(
          event.data.object as Stripe.Invoice
        );
      }
      break;

    default:
      console.log(`Unhandled event type: ${event.type}`);
  }
};
